// app/components/SiteFooter.tsx
import Image from "next/image";
import { ChevronDown, Clock, MapPin, Star } from "lucide-react";
import { Fragment, type ReactNode, type SVGProps } from "react";
import CookiePreferencesLink from "@/app/components/legal/CookiePreferencesLink";
import {
  LAB_LOCATIONS,
  LAB_TIMING_GROUPS,
  LAB_TIMINGS_LAST_UPDATED,
  MAIN_CENTRE,
  ONE_NORTH_EVENTS_OFFICE,
  THEORY_TIMINGS_GROUPED,
  UPCOMING_LAB_LOCATIONS,
  toGoogleMapsSearchUrl,
  type LabTimingSlot,
} from "@/lib/eclat-locations";
import { SOCIAL_CHANNELS, type SocialChannelKey } from "@/lib/social-channels";
import {
  SiFacebook,
  SiInstagram,
  SiThreads,
  SiTiktok,
  SiX,
  SiXiaohongshu,
  SiYoutube,
} from "@icons-pack/react-simple-icons";

function LinkedInIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.226.792 24 1.771 24h20.451C23.2 24 24 23.226 24 22.271V1.729C24 .774 23.2 0 22.222 0H22.225zM7.071 20.452H3.56V9h3.511zM5.315 7.433a2.033 2.033 0 1 1 0-4.066 2.033 2.033 0 0 1 0 4.066zm15.138 13.019h-3.511v-5.6c0-1.336-.027-3.055-1.862-3.055-1.864 0-2.149 1.458-2.149 2.966v5.689H9.42V9h3.37v1.561h.048c.469-.885 1.616-1.817 3.33-1.817 3.56 0 4.213 2.345 4.213 5.395z" />
    </svg>
  );
}

// Per-channel icon nodes, keyed by the registry key. Data (URLs, names, utm
// source) lives in lib/social-channels.ts; the icon components stay here because
// they vary in kind (react-simple-icons, a custom SVG, and an <Image>).
const SOCIAL_ICONS: Record<SocialChannelKey, ReactNode> = {
  instagram: (
    <SiInstagram size={20} title="Instagram" className="inline-block" />
  ),
  tiktok: <SiTiktok size={20} title="TikTok" className="inline-block" />,
  lemon8: (
    <span className="relative block h-5 w-9">
      <Image
        src="/optimised/lemon8-icon-white.png"
        alt=""
        aria-hidden="true"
        fill
        sizes="36px"
        className="object-contain"
      />
    </span>
  ),
  threads: <SiThreads size={20} title="Threads" className="inline-block" />,
  youtube: <SiYoutube size={20} title="YouTube" className="inline-block" />,
  linkedin: <LinkedInIcon className="inline-block h-5 w-5" />,
  facebook: <SiFacebook size={20} title="Facebook" className="inline-block" />,
  xiaohongshu: (
    <SiXiaohongshu size={20} title="Xiaohongshu" className="inline-block" />
  ),
  x: <SiX size={20} title="X" className="inline-block" />,
};

// Lemon8's mark is a white image, so it dims with opacity instead of text colour.
const SOCIAL_TONE_CLASS: Partial<Record<SocialChannelKey, string>> = {
  lemon8: "opacity-75 hover:opacity-100",
};
const DEFAULT_SOCIAL_TONE_CLASS = "text-white/75 hover:text-white";

const FOCUS_RING_CLASS =
  "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

// Footer links stand alone rather than sitting inside prose, so the underline
// appears on hover and focus instead of on every link at rest.
const QUIET_LINK_CLASS = `text-white/85 underline-offset-4 hover:text-white hover:underline ${FOCUS_RING_CLASS}`;

const ICON_CLASS = "mt-1 h-4 w-4 shrink-0 text-white/60";

const PANEL_CLASS =
  "rounded-2xl border border-white/15 bg-white/5 p-5 shadow-[0_18px_40px_rgba(0,0,0,0.18)] backdrop-blur-sm md:p-6";

const SECTION_HEADING_CLASS = "text-xl font-bold text-white md:text-2xl";

function SocialLinks() {
  return (
    // Padding widens each tap target past the 20px icon; the negative margin
    // keeps the first icon on the content edge.
    <div className="-m-1 flex flex-wrap items-center gap-2 sm:gap-3">
      {SOCIAL_CHANNELS.map((channel) => (
        <a
          key={channel.key}
          href={channel.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-ga-event="social_click"
          data-ga-category="social"
          data-ga-action="footer"
          data-ga-label={channel.key}
          data-ga-value={channel.profileUrl}
          aria-label={`Eclat Institute on ${channel.name}`}
          className={`inline-flex items-center justify-center p-1 ${
            SOCIAL_TONE_CLASS[channel.key] ?? DEFAULT_SOCIAL_TONE_CLASS
          } ${FOCUS_RING_CLASS}`}
        >
          {SOCIAL_ICONS[channel.key]}
          <span className="sr-only">
            {channel.srSubject} on {channel.name}
          </span>
        </a>
      ))}
    </div>
  );
}

function toTrackingLabel(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Each time is kept whole on a line, so a narrow column wraps between slots.
type FooterHoursRow = { days: string; times: readonly string[] };

type FooterVenue = {
  label: string;
  slug?: string;
  usageLine?: string;
  // Left out when the same building is already listed with its address.
  place?: {
    addressLines: readonly string[];
    mapUrl: string;
    reviewUrl?: string;
    mapTrackingLabel: string;
    reviewTrackingLabel: string;
  };
  hoursLabel?: string;
  hours: readonly FooterHoursRow[];
};

// Lab rows use the theory rows' format so both panels read the same way:
// schema.org Monday to Friday becomes "Mon-Fri".
function toDayRange(schemaDays: readonly string[]): string {
  const days = schemaDays.map((day) =>
    day.replace("https://schema.org/", "").slice(0, 3)
  );
  return days.length > 1 ? `${days[0]}-${days[days.length - 1]}` : days[0];
}

function toClock(time: string) {
  const [hours, minutes] = time.split(":").map(Number);
  const minuteLabel = minutes ? `:${String(minutes).padStart(2, "0")}` : "";
  return {
    hour: `${hours % 12 || 12}${minuteLabel}`,
    meridiem: hours < 12 ? "am" : "pm",
  };
}

// 12:00 to 14:00 becomes "12-2pm" and 11:00 to 13:00 "11am-1pm", like "4-9pm".
function toSlotLabel({ startTime, endTime }: LabTimingSlot): string {
  const start = toClock(startTime);
  const end = toClock(endTime);
  return start.meridiem === end.meridiem
    ? `${start.hour}-${end.hour}${end.meridiem}`
    : `${start.hour}${start.meridiem}-${end.hour}${end.meridiem}`;
}

function toLabHours(venueLabel: string): FooterHoursRow[] {
  return LAB_TIMING_GROUPS.filter(
    (group) => group.venue === venueLabel
  ).flatMap((group) =>
    group.schedule.map(({ schemaDays, slots }) => ({
      days: toDayRange(schemaDays),
      times: slots.map(toSlotLabel),
    }))
  );
}

const THEORY_VENUES: readonly FooterVenue[] = [
  {
    label: MAIN_CENTRE.label,
    slug: MAIN_CENTRE.slug,
    place: {
      addressLines: MAIN_CENTRE.addressLines,
      mapUrl: MAIN_CENTRE.gbpShareUrl,
      reviewUrl: MAIN_CENTRE.gbpReviewUrl,
      mapTrackingLabel: "main_centre_map",
      reviewTrackingLabel: "main_centre_review",
    },
    hoursLabel: "Lesson times",
    hours: THEORY_TIMINGS_GROUPED.map(({ days, hours }) => ({
      days,
      times: [hours],
    })),
  },
  {
    label: ONE_NORTH_EVENTS_OFFICE.label,
    slug: ONE_NORTH_EVENTS_OFFICE.slug,
    usageLine: ONE_NORTH_EVENTS_OFFICE.usageLine,
    place: {
      addressLines: ONE_NORTH_EVENTS_OFFICE.addressLines,
      mapUrl: ONE_NORTH_EVENTS_OFFICE.gbpShareUrl,
      reviewUrl: ONE_NORTH_EVENTS_OFFICE.gbpReviewUrl,
      mapTrackingLabel: "one_north_events_office_map",
      reviewTrackingLabel: "one_north_events_office_review",
    },
    hours: [],
  },
];

// Jurong East's page, address and review links already sit under Theory
// Centre, so its lab entry shows only what differs: subjects, days and times.
const LAB_VENUES: readonly FooterVenue[] = [
  ...LAB_LOCATIONS,
  ...UPCOMING_LAB_LOCATIONS,
].map((location) => {
  const listedUnderTheory = location.label === MAIN_CENTRE.label;
  return {
    label: location.label,
    slug: listedUnderTheory ? undefined : location.slug,
    usageLine: location.usageLine,
    place: listedUnderTheory
      ? undefined
      : {
          addressLines: location.addressLines,
          mapUrl:
            location.gbpShareUrl ??
            toGoogleMapsSearchUrl(location.addressLines),
          reviewUrl: location.gbpReviewUrl,
          mapTrackingLabel: `${toTrackingLabel(location.label)}_lab_map`,
          reviewTrackingLabel: `${toTrackingLabel(location.label)}_lab_review`,
        },
    hoursLabel: "Practical times",
    hours: toLabHours(location.label),
  };
});

const LAB_COLUMNS = [LAB_VENUES.slice(0, 1), LAB_VENUES.slice(1)] as const;

const FOOTER_NAV_GROUPS = [
  {
    heading: "JC Tuition",
    links: [
      {
        href: "/blog/a-level-maths-tuition",
        label: "H2 Maths Tuition",
        trackingLabel: "programme_h2_maths",
      },
      {
        href: "/blog/a-level-physics-tuition",
        label: "H2 Physics Tuition",
        trackingLabel: "programme_h2_physics",
      },
      {
        href: "/blog/a-level-chemistry-tuition",
        label: "H2 Chemistry Tuition",
        trackingLabel: "programme_h2_chemistry",
      },
      {
        href: "/blog/a-level-biology-tuition",
        label: "H2 Biology Tuition",
        trackingLabel: "programme_h2_biology",
      },
    ],
  },
  {
    heading: "IP Tuition",
    links: [
      {
        href: "/blog/ip-lower-secondary-maths-tuition",
        label: "IP Lower Sec Maths",
        trackingLabel: "programme_ip_lower_secondary_maths",
      },
      {
        href: "/blog/ip-lower-secondary-science-tuition",
        label: "IP Lower Sec Science",
        trackingLabel: "programme_ip_lower_secondary_science",
      },
      {
        href: "/blog/ip-upper-secondary-maths-tuition",
        label: "IP Upper Sec Maths",
        trackingLabel: "programme_ip_upper_secondary_maths",
      },
      {
        href: "/blog/ip-upper-secondary-physics-tuition",
        label: "IP Upper Sec Physics",
        trackingLabel: "programme_ip_upper_secondary_physics",
      },
      {
        href: "/blog/ip-upper-secondary-chemistry-tuition",
        label: "IP Upper Sec Chemistry",
        trackingLabel: "programme_ip_upper_secondary_chemistry",
      },
      {
        href: "/blog/ip-upper-secondary-biology-tuition",
        label: "IP Upper Sec Biology",
        trackingLabel: "programme_ip_upper_secondary_biology",
      },
    ],
  },
  {
    heading: "Explore",
    links: [
      {
        href: "/blog",
        label: "Study Resources",
        trackingLabel: "study_resources",
      },
      {
        href: "/tuition",
        label: "All Tuition Programmes",
        trackingLabel: "programme_all_tuition",
      },
      {
        href: "/tutors",
        label: "Our Tutors",
        trackingLabel: "programme_our_tutors",
      },
    ],
  },
  {
    heading: "Eclat Institute",
    links: [
      { href: "/events", label: "Events", trackingLabel: "events" },
      { href: "/support", label: "Support", trackingLabel: "support" },
      {
        href: "/partnerships",
        label: "Partnerships",
        trackingLabel: "partnerships",
      },
      { href: "/careers", label: "Careers", trackingLabel: "careers" },
      { href: "/media", label: "Media", trackingLabel: "media" },
      { href: "/legal", label: "Legal", trackingLabel: "legal" },
    ],
  },
] as const;

function FooterVenueBlock({ venue }: { venue: FooterVenue }) {
  const pageHref = venue.slug ? `/locations/${venue.slug}` : null;
  const { place } = venue;
  const hours =
    venue.hours.length > 0 ? (
      <dl className="space-y-0.5">
        {venue.hours.map(({ days, times }) => (
          <div key={days} className="grid grid-cols-[4rem_1fr] gap-x-3">
            <dt className="font-medium text-white">{days}</dt>
            <dd className={times[0] === "Closed" ? "text-white/60" : ""}>
              {times.map((time, index) => (
                <Fragment key={time}>
                  {index > 0 ? ", " : null}
                  <span className="whitespace-nowrap">{time}</span>
                </Fragment>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    ) : null;

  return (
    <div>
      <p className="text-base font-semibold leading-snug text-white md:text-lg">
        {pageHref ? (
          <a
            href={pageHref}
            data-ga-event="footer_nav_click"
            data-ga-category="navigation"
            data-ga-action="footer"
            data-ga-label={`location_${venue.slug}`}
            data-ga-value={pageHref}
            className={`underline-offset-4 hover:underline ${FOCUS_RING_CLASS}`}
          >
            {venue.label}
          </a>
        ) : (
          venue.label
        )}
      </p>
      {venue.usageLine ? (
        <p className="mt-1 text-sm font-medium leading-snug text-amber-100">
          {venue.usageLine}
        </p>
      ) : null}
      <div className="mt-3 space-y-2 text-sm leading-6 text-white/85">
        {place ? (
          <a
            href={place.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-ga-event="location_map_click"
            data-ga-category="locations"
            data-ga-action="footer"
            data-ga-label={place.mapTrackingLabel}
            data-ga-value={place.mapUrl}
            aria-label={`${venue.label}, ${place.addressLines.join(
              ", "
            )} (opens in Google Maps)`}
            className={`flex gap-2.5 ${QUIET_LINK_CLASS}`}
          >
            <MapPin aria-hidden="true" className={ICON_CLASS} />
            <span>
              {place.addressLines.map((line, index) => (
                <span key={`${index}-${line}`} className="block">
                  {line}
                </span>
              ))}
            </span>
          </a>
        ) : null}
        {place?.reviewUrl ? (
          <a
            href={place.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-ga-event="location_review_click"
            data-ga-category="locations"
            data-ga-action="footer"
            data-ga-label={place.reviewTrackingLabel}
            data-ga-value={place.reviewUrl}
            aria-label={`Write a review of ${venue.label} on Google`}
            className={`flex w-fit gap-2.5 ${QUIET_LINK_CLASS}`}
          >
            <Star aria-hidden="true" className={ICON_CLASS} />
            Write a review
          </a>
        ) : null}
        {hours ? (
          <>
            {/* Phones fold the times so the venue list stays short; wider
                screens have room to show them open. */}
            <details className="group md:hidden">
              <summary
                className={`flex w-fit cursor-pointer list-none items-center gap-2.5 [&::-webkit-details-marker]:hidden ${QUIET_LINK_CLASS}`}
              >
                <Clock
                  aria-hidden="true"
                  className="h-4 w-4 shrink-0 text-white/60"
                />
                {venue.hoursLabel}
                <ChevronDown
                  aria-hidden="true"
                  className="h-4 w-4 text-white/60 transition-transform group-open:rotate-180"
                />
              </summary>
              <div className="mt-2 pl-[1.625rem]">{hours}</div>
            </details>
            <div className="hidden gap-2.5 md:flex">
              <Clock aria-hidden="true" className={ICON_CLASS} />
              {hours}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

export default function SiteFooter() {
  return (
    <footer
      data-nosnippet
      className="relative overflow-hidden bg-gradient-to-r from-blue-950 via-blue-900 to-blue-950 py-12 text-white"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            "image-set(url('/optimised/footer-noise-20251104-1f5d7d56.avif') type('image/avif') 1x, url('/optimised/footer-noise-20251104-1f5d7d56.webp') type('image/webp') 1x, url('/optimised/footer-noise-20251104-1f5d7d56.png') type('image/png') 1x)",
          backgroundSize: "128px 128px",
          backgroundRepeat: "repeat",
        }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-blue-950/60"
        aria-hidden="true"
      />
      {/* Same container as the site header, so both share a left edge. */}
      <div className="relative mx-auto max-w-5xl px-4 xl:max-w-7xl">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <section
            aria-labelledby="footer-theory-centre"
            className={PANEL_CLASS}
          >
            <p id="footer-theory-centre" className={SECTION_HEADING_CLASS}>
              Theory Centre
            </p>
            <div className="mt-5 space-y-6">
              {THEORY_VENUES.map((venue) => (
                <FooterVenueBlock key={venue.label} venue={venue} />
              ))}
            </div>
          </section>

          <section
            aria-labelledby="footer-practical-labs"
            className={PANEL_CLASS}
          >
            <p id="footer-practical-labs" className={SECTION_HEADING_CLASS}>
              Practical Labs
            </p>
            <div className="mt-5 grid gap-x-8 gap-y-6 sm:grid-cols-2">
              {LAB_COLUMNS.map((column, columnIndex) => (
                <div key={columnIndex} className="space-y-6">
                  {column.map((venue) => (
                    <FooterVenueBlock key={venue.label} venue={venue} />
                  ))}
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-white/60">
              Timings last updated: {LAB_TIMINGS_LAST_UPDATED.label}. Confirm
              the venue and exact session before travelling.
            </p>
          </section>
        </div>

        <nav
          className="mt-10 grid grid-cols-2 gap-x-8 gap-y-8 border-t border-white/15 pt-10 text-sm sm:grid-cols-4"
          aria-label="Footer navigation"
        >
          {FOOTER_NAV_GROUPS.map((group) => (
            <div key={group.heading}>
              <p className="font-semibold text-white">{group.heading}</p>
              <div className="mt-3 space-y-2">
                {group.links.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    data-ga-event="footer_nav_click"
                    data-ga-category="navigation"
                    data-ga-action="footer"
                    data-ga-label={item.trackingLabel}
                    data-ga-value={item.href}
                    className={`block w-fit ${QUIET_LINK_CLASS}`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="mt-10 flex flex-col gap-6 border-t border-white/15 pt-8 text-sm text-white/60 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-1">
            <p>&copy; 2026 Eclat Institute. All rights reserved.</p>
            <p>
              Empowering Singapore&rsquo;s IP students to reach their fullest
              potential
            </p>
            <p>
              <CookiePreferencesLink className={QUIET_LINK_CLASS} />
            </p>
          </div>
          <SocialLinks />
        </div>
      </div>
    </footer>
  );
}
